import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Star, Award, Phone } from "lucide-react";
import { MentorMatchQuestionnaire } from "./MentorMatchQuestionnaire";
import MentorProfileModal from "./MentorProfileModal";
import { safeGetItem, safeSetItem } from "@/lib/safe-storage";
import { MentorTierBadge } from "./MentorTierBadge";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface RecommendedMentorsSectionProps {
  onBookMentor: (mentor: any) => void;
}

export const RecommendedMentorsSection: React.FC<RecommendedMentorsSectionProps> = ({ 
  onBookMentor 
}) => {
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [recommendations, setRecommendations] = useState<any[]>([]);
  const [hasCompletedQuestionnaire, setHasCompletedQuestionnaire] = useState(false);
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);
  const [selectedMentorId, setSelectedMentorId] = useState<string | null>(null);

  useEffect(() => {
    checkQuestionnaireStatus();
  }, []);

  useEffect(() => {
    if (hasCompletedQuestionnaire) {
      checkAndRefreshRecommendations();
    }
  }, [hasCompletedQuestionnaire]);

  const checkQuestionnaireStatus = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        setIsLoadingStatus(false);
        return;
      }

      // Check database for completion status
      const { data: credits } = await supabase
        .from('student_credits')
        .select('questionnaire_completed')
        .eq('user_id', session.user.id)
        .maybeSingle();

      if (credits?.questionnaire_completed) {
        setHasCompletedQuestionnaire(true);
        // Load stored recommendations
        const stored = safeGetItem('mentor_recommendations');
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            setRecommendations(parsed);
          } catch (e) {
            console.error('Error parsing stored recommendations:', e);
          }
        }
      }
    } catch (error) {
      console.error('Error checking questionnaire status:', error);
    } finally {
      setIsLoadingStatus(false);
    }
  };

  const checkAndRefreshRecommendations = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('refresh-mentor-recommendations');

      if (error) {
        console.error('Error checking recommendations:', error);
        return;
      }

      if (data?.recommendations && data.recommendations.length > 0) {
        setRecommendations(data.recommendations);
        safeSetItem('mentor_recommendations', JSON.stringify(data.recommendations));

        if (data.refreshed) {
          toast({
            title: "New Recommendations! 🎯",
            description: "Your mentor matches have been refreshed with new suggestions.",
          });
        }
      }
    } catch (error) {
      console.error('Error refreshing recommendations:', error);
    }
  };

  const handleQuestionnaireComplete = (recs: any[]) => {
    setRecommendations(recs);
    setHasCompletedQuestionnaire(true);
    setShowQuestionnaire(false);
    
    // Store recommendations in localStorage
    safeSetItem('mentor_recommendations', JSON.stringify(recs));
  };

  if (isLoadingStatus) {
    return (
      <Card className="obsidian-card border-primary/30">
        <CardContent className="p-6">
          <div className="text-center py-8">
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show questionnaire only if user hasn't completed it
  if (!hasCompletedQuestionnaire && !showQuestionnaire) {
    return (
      <MentorMatchQuestionnaire
        onComplete={handleQuestionnaireComplete}
        onCancel={() => setShowQuestionnaire(false)}
        isRetake={false}
      />
    );
  }

  if (showQuestionnaire) {
    return (
      <MentorMatchQuestionnaire
        onComplete={handleQuestionnaireComplete}
        onCancel={() => {
          setShowQuestionnaire(false);
          if (recommendations.length > 0) {
            setHasCompletedQuestionnaire(true);
          }
        }}
        isRetake={true}
      />
    );
  }

  // Show recommendations
  return (
    <Card className="obsidian-card border-primary/30 bg-gradient-to-br from-primary/10 via-purple-500/10 to-primary/10 animate-fade-in">
      <CardHeader>
        <div>
          <CardTitle>
            Your Recommended Mentors
          </CardTitle>
          <CardDescription className="mt-1">
            Personalized matches based on your goals • One-time AI matching
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className={isMobile ? "p-3" : "p-6"}>
        <div className="space-y-4">
          {recommendations.map((rec) => (
            <div 
              key={rec.mentor_id} 
              className="obsidian-card border border-primary/20 rounded-lg p-4 hover:border-primary/40 transition-colors"
            >
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                {/* Left: Avatar & Info */}
                <div className="flex items-start gap-3 lg:flex-1">
                  <Avatar className="w-16 h-16 border-2 border-primary/20 flex-shrink-0">
                    <AvatarImage 
                      src={rec.mentor.profile_image_url} 
                      alt={`${rec.mentor.first_name} ${rec.mentor.last_name}`}
                    />
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {rec.mentor.first_name?.[0]}{rec.mentor.last_name?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-lg">
                      {rec.mentor.first_name} {rec.mentor.last_name}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      {rec.mentor.specialty}
                    </p>
                    
                    {/* Tier, Rating & Discovery Badge */}
                    <div className="flex items-center gap-2 flex-wrap mt-2">
                      <MentorTierBadge tier={rec.mentor.tier} size="sm" />
                      {rec.mentor.rating > 0 && (
                        <Badge variant="secondary" className="gap-1">
                          <Star className="w-3 h-3 fill-primary text-primary" />
                          {rec.mentor.rating.toFixed(1)}
                        </Badge>
                      )}
                      {rec.mentor.discovery_call_enabled && (
                        <Badge variant="outline" className="gap-1 border-green-500/50 text-green-600 dark:text-green-400">
                          <Phone className="w-3 h-3" />
                          Free Consult
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>

                {/* Center: Match Score & Reasoning */}
                <div className="lg:flex-[2] space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 bg-primary/20 rounded-full h-2 overflow-hidden">
                      <div 
                        className="bg-primary h-full transition-all"
                        style={{ width: `${rec.match_score}%` }}
                      />
                    </div>
                    <span className="text-sm font-semibold text-primary whitespace-nowrap">
                      {rec.match_score}% Match
                    </span>
                  </div>
                  
                  <p className="text-sm text-muted-foreground">
                    {rec.reasoning}
                  </p>

                  {/* Key Strengths */}
                  {rec.key_strengths && rec.key_strengths.length > 0 && (
                    <div className="space-y-1.5">
                      <p className="text-xs font-semibold text-primary flex items-center gap-1">
                        <Award className="w-3 h-3" />
                        Why This Match:
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {rec.key_strengths.slice(0, 3).map((strength: string, idx: number) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {strength}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Right: Action Buttons */}
                <div className="flex flex-col gap-2 lg:w-40">
                  <Button 
                    variant="outline"
                    className="w-full" 
                    onClick={() => setSelectedMentorId(rec.mentor.id)}
                  >
                    View Profile
                  </Button>
                  <Button 
                    className="w-full" 
                    onClick={() => onBookMentor(rec.mentor)}
                  >
                    Book Session
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {recommendations.length === 0 && (
          <div className="text-center py-8 space-y-4">
            <div className="text-muted-foreground">
              <p className="font-semibold">No mentor recommendations available.</p>
              <p className="text-sm mt-2">Your previous recommendations expired. Please retake the questionnaire to get personalized matches.</p>
            </div>
            <Button 
              onClick={() => setShowQuestionnaire(true)}
              className="mx-auto"
            >
              Retake Questionnaire
            </Button>
          </div>
        )}
      </CardContent>

      {selectedMentorId && (
        <MentorProfileModal
          mentorId={selectedMentorId}
          onClose={() => setSelectedMentorId(null)}
          onBookSession={(mentor) => {
            setSelectedMentorId(null);
            onBookMentor(mentor);
          }}
        />
      )}
    </Card>
  );
};
